import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { HrAdminApprovalFields } from '../../components/HrAdminApprovalFields';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getWfhModuleCapabilities } from '../../config/wfhCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  cancelWfhRequest,
  getWfhRequestDetails,
} from '../../services/wfhService';
import type { WfhRequestDetails } from '../../types/wfh';
import type { WorkflowDetails } from '../../types/workflow';
import { wfhErrorMessage } from '../../utils/wfhErrors';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhDetails'>;

function toApprovalDetails(wfh: WfhRequestDetails): WorkflowDetails {
  return {
    workflowInstanceId: wfh.workflow?.workflowInstanceId ?? 0,
    requestTypeCode: 'WFH_REQUEST',
    requestTypeLabel: 'Work from home request',
    requestReferenceId: wfh.wfhRequestId,
    requestNumber: wfh.requestNumber,
    employee: {
      employeeId: wfh.employee.employeeId,
      employeeNumber: wfh.employee.employeeNumber,
      displayName: wfh.employee.displayName,
      departmentName: null,
      designationName: wfh.employee.designationName,
    },
    workflowStatusCode: wfh.statusCode,
    workflowStatusLabel: wfh.statusLabel,
    submittedAt: wfh.submittedAt,
    completedAt: null,
    rejectReason: wfh.rejectReason,
    cancellationReason: wfh.cancellationReason,
    currentStep: wfh.workflow
      ? {
          stepId: null,
          stepName: wfh.workflow.currentStepName,
          stepNumber: null,
        }
      : null,
    steps: [],
    requestDetails: {
      fromDate: wfh.fromDate,
      toDate: wfh.toDate,
      totalDays: wfh.totalDays,
      requestType: wfh.requestType,
      sessionType: wfh.sessionType,
      sessionName: wfh.sessionName,
      durationName: wfh.durationName,
      isHalfDay: wfh.isHalfDay,
      reason: wfh.reason,
      workLocation: wfh.workLocation,
      availabilityNotes: wfh.availabilityNotes,
      cancellationReason: wfh.cancellationReason,
    },
    canApprove: false,
    canReject: false,
  };
}

export function WfhRequestDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getWfhModuleCapabilities(auth.permissions);
  const canAccess =
    auth.hasPermission(PERMISSIONS.WFH_APPLY) ||
    auth.hasPermission(PERMISSIONS.WFH_APPROVE);
  const wfhRequestId = route.params.wfhRequestId;

  const [details, setDetails] = useState<WfhRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    if (!Number.isInteger(wfhRequestId) || wfhRequestId <= 0) {
      setDetails(null);
      setError('WFH request was not found.');
      setLoading(false);
      return;
    }
    if (!capabilities.canViewDetails) {
      setDetails(null);
      setError('WFH request details are not currently available.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getWfhRequestDetails(wfhRequestId);
      setDetails(result);
    } catch (caught) {
      setDetails(null);
      setError(
        wfhErrorMessage(caught, 'Unable to load WFH request details.'),
      );
    } finally {
      setLoading(false);
    }
  }, [canAccess, capabilities.canViewDetails, wfhRequestId]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const confirmCancel = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setActionError('Cancellation reason is required.');
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      await cancelWfhRequest(wfhRequestId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        wfhErrorMessage(caught, 'Unable to cancel WFH request.'),
      );
    } finally {
      setCancelling(false);
    }
  };

  // Always show the request owner — never the active role / logged-in viewer.
  const employeeName =
    details?.employee.displayName?.trim() ||
    details?.employee.employeeNumber?.trim() ||
    'WFH Details';
  const approvalDetails = details ? toApprovalDetails(details) : null;

  return (
    <PermissionGuard
      permission={PERMISSIONS.WFH_APPLY}
      anyPermissions={[PERMISSIONS.WFH_APPROVE]}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="WFH Details"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading WFH details" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="WFH Details"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details && approvalDetails ? (
        <AppScreen scroll>
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.statusLabel}
                  statusCode={details.statusCode}
                />
              </View>

              <HrAdminApprovalFields
                details={approvalDetails}
                palette={palette}
              />
            </AppCard>

            {capabilities.canCancel && details.canCancel ? (
              showCancelReason ? (
                <View style={styles.cancelBlock}>
                  <AppInput
                    label="Cancellation reason"
                    value={cancelReason}
                    onChangeText={value => {
                      setCancelReason(value);
                      setActionError(null);
                    }}
                    placeholder="Enter reason for cancellation"
                  />
                  {actionError ? (
                    <Text accessibilityRole="alert" style={themed.actionError}>
                      {actionError}
                    </Text>
                  ) : null}
                  <AppButton
                    title="Confirm Cancel"
                    variant="danger"
                    onPress={confirmCancel}
                    loading={cancelling}
                    disabled={cancelling}
                  />
                  <AppButton
                    title="Keep WFH"
                    variant="text"
                    onPress={() => {
                      setShowCancelReason(false);
                      setCancelReason('');
                      setActionError(null);
                    }}
                    disabled={cancelling}
                  />
                </View>
              ) : (
                <AppButton
                  title="Cancel WFH"
                  variant="danger"
                  onPress={() => setShowCancelReason(true)}
                />
              )
            ) : null}
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  profileText: { flex: 1, gap: 2 },
  cancelBlock: { gap: spacing.md, marginBottom: spacing.xl },
});
